import { onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAutopilotStore } from '@llmor/autopilot-vue';
import type { AutopilotFunctionContext, AutopilotFunctionInput } from '@/autopilot/functions';

/**
 * Binds a set of domain-specific autopilot functions to the calling component's lifecycle:
 * they are registered during setup and unregistered on unmount, so a function is only available
 * while the page/feature that owns it is active.
 *
 * Accepts ready definitions (page-local deps already bound) or factories that build one from
 * the app context (e.g. needing `router`).
 *
 * Register during setup (not onMounted) so a route-readiness `ready()` that fires on the next
 * tick still sees these tools. Registering before/without a relay connection is harmless — the
 * client registry persists and syncs on connect.
 */
export function useAutopilotFunctions(functions: AutopilotFunctionInput[]) {
  const store = useAutopilotStore();
  const router = useRouter();
  const ctx: AutopilotFunctionContext = { router };
  const names: string[] = [];

  for (const fn of functions) {
    const def = typeof fn === 'function' ? fn(ctx) : fn;
    store.client.registerFunction(def);
    names.push(def.name);
  }

  onUnmounted(() => {
    for (const name of names) {
      store.client.unregisterFunction(name);
    }
  });
}
